import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { AuthProvider } from "./contexts/AuthContext";
import { ModeProvider } from "./contexts/Mode";
import { ToastProvider } from "./contexts/ToastProvider";
import { TutorialProvider } from "./contexts/TutorialContext";
import ProtectedRoute from "./components/collavue/ProtectedRoute";
import AppShell from "./components/collavue/AppShell";
import TutorialOverlay from "./components/collavue/TutorialOverlay";

import LoginPage from "./pages/collavue/LoginPage";
import SignUpPage from "./pages/collavue/SignUpPage";
import DashboardPage from "./pages/collavue/DashboardPage";
import CampaignsPage from "./pages/collavue/CampaignsPage";
import NewCampaignPage from "./pages/collavue/NewCampaignPage";
import CampaignDetailPage from "./pages/collavue/CampaignDetailPage";
import LeadsInboxPage from "./pages/collavue/LeadsInboxPage";
import LeadDetailPage from "./pages/collavue/LeadDetailPage";
import BrandVoicePage from "./pages/collavue/BrandVoicePage";
import BillingPage from "./pages/collavue/BillingPage";
import NotificationsPage from "./pages/collavue/NotificationsPage";
import SettingsPage from "./pages/collavue/SettingsPage";
import AdminPage from "./pages/collavue/AdminPage";
import Landing from "./pages/Landing";
import BookDemoPage from "./pages/BookDemoPage";

// Firebase Auth email-action handlers (email verification, password reset).
// These must be reachable so links sent from verification/reset emails work.
import AuthAction from "./pages/AuthAction";
import ResendVerification from "./pages/ResendVerfication";
import ResetPassword from "./components/Auth/ResetPassword";

import AlternativesIndex from "./pages/alternatives/AlternativesIndex";
import AlternativePage from "./pages/alternatives/AlternativePage";
import Pricing from "./pages/marketing/Pricing";
import ReplyAgent from "./pages/marketing/ReplyAgent";
import LeadInbox from "./pages/marketing/LeadInbox";
import BrandVoiceFeature from "./pages/marketing/BrandVoiceFeature";
import Product from "./pages/marketing/Product";
import CaseStudies from "./pages/marketing/CaseStudies";
import Docs from "./pages/marketing/Docs";
import About from "./pages/marketing/About";
import Blog from "./pages/marketing/Blog";
import Careers from "./pages/marketing/Careers";
import PressPage from "./pages/marketing/Press";
import LegalPage from "./pages/marketing/Legal";
import FindSponsors from "./pages/blog/FindSponsors";
import LeadGenHub from "./pages/guides/LeadGenHub";
import LeadGuidePage from "./pages/guides/LeadGuidePage";
import ScrollToTop from "./components/marketing/ScrollToTop";

function Shell({ children }) {
  return (
    <AppShell>
      {children}
      <TutorialOverlay />
    </AppShell>
  );
}

// Stripe checkout success/cancel URLs historically pointed at /credits, which
// has no route — the catch-all bounced paying customers to the marketing
// homepage with zero confirmation. Forward them to Billing, keeping the
// ?status=success|cancel query so the banner renders.
function CreditsRedirect() {
  const location = useLocation();
  return <Navigate to={`/billing${location.search}`} replace />;
}

// function ConditionalAppNav() {
//   const location = useLocation();
//   if (location.pathname === "/") return null;
//   return <AppNav />;
// }

export default function App() {
  return (
    <ModeProvider>
      <AuthProvider>
        <TutorialProvider>
          <ToastProvider>
            <Router>
            <ScrollToTop />
            <Routes>
              {/* Public */}
                {/* <ConditionalAppNav /> */}

              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />

              {/* Firebase Auth email-action handlers. The verification/reset
                  emails link here; without these routes the link falls through
                  to the catch-all below and silently redirects to "/", so the
                  email never gets verified. */}
              <Route path="/auth/action" element={<AuthAction />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Marketing */}
              <Route path="/product" element={<Product />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/reply-agent" element={<ReplyAgent />} />
              <Route path="/lead-inbox" element={<LeadInbox />} />
              <Route path="/brand-voice-feature" element={<BrandVoiceFeature />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/find-sponsors" element={<FindSponsors />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/dpa" element={<LegalPage kind="dpa" />} />
              <Route path="/status" element={<LegalPage kind="status" />} />

              {/* Lead generation guides — the organic search entry points.
                  Alias URLs are 301-redirected in firebase.json; the
                  client-side redirects below keep local dev and in-app links
                  landing on the canonical URL instead of the catch-all. */}
              <Route path="/lead-generation" element={<LeadGenHub />} />
              <Route
                path="/twitter-lead-generation"
                element={<LeadGuidePage guideKey="twitter" />}
              />
              <Route
                path="/linkedin-lead-generation"
                element={<LeadGuidePage guideKey="linkedin" />}
              />
              <Route
                path="/reddit-lead-generation"
                element={<LeadGuidePage guideKey="reddit" />}
              />
              <Route
                path="/how-to-find-leads-on-twitter"
                element={<Navigate to="/twitter-lead-generation" replace />}
              />
              <Route
                path="/x-lead-generation"
                element={<Navigate to="/twitter-lead-generation" replace />}
              />
              <Route
                path="/how-to-find-leads-on-linkedin"
                element={<Navigate to="/linkedin-lead-generation" replace />}
              />
              <Route
                path="/linkedin-prospecting"
                element={<Navigate to="/linkedin-lead-generation" replace />}
              />
              <Route
                path="/how-to-find-leads-on-reddit"
                element={<Navigate to="/reddit-lead-generation" replace />}
              />
              <Route
                path="/reddit-marketing"
                element={<Navigate to="/reddit-lead-generation" replace />}
              />
              <Route
                path="/subreddit-prospecting"
                element={<Navigate to="/reddit-lead-generation" replace />}
              />
              <Route
                path="/social-selling"
                element={<Navigate to="/lead-generation" replace />}
              />

              {/* Alternatives */}
              <Route path="/alternatives" element={<AlternativesIndex />} />
              <Route path="/alternatives/:slug" element={<AlternativePage />} />

              {/* App (protected) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <DashboardPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <CampaignsPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/new"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <NewCampaignPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/:id"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <CampaignDetailPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <LeadsInboxPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads/:id"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <LeadDetailPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brand-voice"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <BrandVoicePage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route path="/credits" element={<CreditsRedirect />} />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <BillingPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <NotificationsPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Shell>
                      <SettingsPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <Shell>
                      <AdminPage />
                    </Shell>
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Router>
        </ToastProvider>
        </TutorialProvider>
      </AuthProvider>
    </ModeProvider>

  );
}
